/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import React, { FC, useMemo, useRef } from 'react';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, TooltipItem } from 'chart.js';
import clsx from 'clsx';
import { Line } from 'react-chartjs-2';
import { useIntl } from 'react-intl';

import { generateDefaultColors } from '@futrli/predict/report-builder/utils-colors';
import { noDataColors } from '@futrli/predict/report-builder/constants';
import { ROUNDING_OPTIONS, VALUE_DISPLAY_OPTIONS } from '@futrli/predict/report-builder/types';
import { useI18nConstants } from '@futrli/predict/report-builder/use-i18n-constants';

import styles from './chart.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend);
ChartJS.defaults.color = '#7C879D';
ChartJS.defaults.font = {
  family: 'SageUI',
  size: 10,
  weight: '500'
};

interface ChartData {
  labels?: string[];
  datasets?: {
    label: string;
    data?: string[];
  };
}

interface Props {
  comparisonMetricName?: string;
  data: ChartData[];
  isLoading: boolean;
}

export const ForecastingLineChart: FC<Props> = ({ comparisonMetricName, data, isLoading }: Props) => {
  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const chartColors = useMemo(() => generateDefaultColors(data.length), [data.length]);
  const intl = useIntl();
  const { roundAndFormatValue } = useI18nConstants();
  const formatNumber = (val: number | string) =>
    roundAndFormatValue(val, ROUNDING_OPTIONS.INTEGER, VALUE_DISPLAY_OPTIONS.NUMBER, intl, '');
  const formatTooltipLabel = (ctx: TooltipItem<'line'>) => `${ctx.dataset.label || ''}: ${formatNumber(ctx.parsed.y)}`;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false
      },
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: formatTooltipLabel
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
          //   drawBorder: true,
          //   borderWidth: 2,
          //   drawTicks: false
        },
        ticks: {
          display: true,
          color: 'rgba(0, 0, 0, 0.9)'
        }
      },
      y: {
        grid: {
          display: true,
          drawBorder: true,
          borderWidth: 2,
          color: '#DFE3EC'
        },
        ticks: {
          display: true,
          color: 'rgba(0, 0, 0, 0.65)',
          callback: (val: string | number): string => `  ${intl.formatNumber(+val)}`
        }
      }
    }
  };

  const lineData =
    isLoading || !data.length
      ? {
          labels: Array.from({ length: 6 }).map(() => ''),
          datasets: Array.from({ length: 2 }).map((_, i) => ({
            label: `${i}`,
            data: Array.from({ length: 6 }).map(() => 1000 + Math.random() * 15000),
            backgroundColor: noDataColors[i],
            borderColor: noDataColors[i],
            borderWidth: 2
          }))
        }
      : {
          labels: data[0]?.labels,
          datasets: data
            .map((d, i) => {
              const color = chartColors[i];
              return {
                label: d.datasets?.label,
                data: d?.datasets?.data?.map(d => +d),
                backgroundColor: color,
                borderColor: color,
                borderWidth: 2
              };
            })
            .filter(e => e !== null) as { label: string; data: number[] }[]
        };

  return (
    <div className={styles.chartContainer}>
      <div className={styles.title} style={{ marginBottom: 10 }}>
        {comparisonMetricName || ''}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
        {data.map((d, i) => (
          <div className={styles.legendItem} key={`${d.datasets?.label}-${i}`}>
            <div className={styles.legendMark} style={{ backgroundColor: chartColors[i] }} />
            <div>{d.datasets?.label}</div>
          </div>
        ))}
      </div>
      <div className={clsx(styles.chartWrapper)}>
        <Line
          ref={chartRef}
          options={options}
          data={lineData}
          aria-label={intl.formatMessage({
            id: 'predict.forecasting_dashboard_line_chart.aria_label',
            defaultMessage: 'Forecasting dashboard line chart'
          })}
        />
      </div>
    </div>
  );
};
